import { RecordCollection } from '../../app/record-collection.js';
import { CollectionViewPicker } from '../../app/collection-view.js';
import { matchesListSearch } from '../../lib/list-search.js';
import { RegexGuide } from '../../app/regex-guide.js';
import {
  Button,
  Card,
  CurrencyInput,
  Input,
  Modal,
  Select,
  StatusPill,
  Textarea,
  formatCivilDate,
  formatCurrency,
  formatMonth,
  useSnackbar,
  type CivilDate,
  IconButton,
} from '@hue/design-system';
import {
  RuleValidationError,
  assertTypedPattern,
  buildSimplePattern,
  normalizeTerm,
  prepareRule,
  trialOf,
  type PreparedRule,
  type RuleTrialOutcome,
} from '@hue/contracts';
import { keepPreviousData, useQuery, useQueryClient } from '@tanstack/react-query';
import { ArrowDown, ArrowUp, PencilLine, Plus, Trash2, Wand2 } from 'lucide-react';
import { useMemo, useState } from 'react';
import {
  getApiError,
  reconciliationRulesApi,
  type Currency,
  type ReconciliationRuleTarget,
  type FinanceAccount,
  type ReconciliationRule,
  type ReconciliationRuleFamily,
  type ReconciliationRuleInput,
  type ReconciliationRuleOperator,
} from '../../lib/api.js';
import { useOperationalPeriod } from '../../app/operational-period-context.js';
import {
  ListFilterBar,
  EmptyCard,
  ErrorCard,
  LoadingCard,
  RequestAlert,
} from '../../app/data-view.js';

export interface ReconciliationRuleCapabilities {
  canManage: boolean;
}

const operators: Array<{ value: ReconciliationRuleOperator; label: string }> = [
  { value: 'CONTAINS', label: 'contém' },
  { value: 'STARTS_WITH', label: 'começa com' },
  { value: 'ENDS_WITH', label: 'termina com' },
  { value: 'EQUALS', label: 'é exatamente' },
  { value: 'ANY_OF', label: 'contém uma destas expressões' },
];

const operatorLabels = new Map(operators.map((item) => [item.value, item.label]));

/// O testador varre o recorte inteiro de uma vez em vez de paginar: mostrar só o que casou seria
/// mentira dentro de uma página, porque o casamento pode estar na seguinte. O teto protege o
/// "todos os períodos" e, quando ele corta, a tela diz quantos movimentos olhou.
const SCAN_LIMIT = 200;
const MAX_MATCHES = 20;
const MAX_SAMPLES = 20;

/// Uma linha do teste: o texto avaliado e o que a fila decidiu sobre ele.
interface Trial {
  key: string;
  text: string;
  occurredOn: string | null;
  amountCents: number | null;
  currency: Currency | null;
  outcome: RuleTrialOutcome;
  /// A regra que vem antes na fila quando a regra em edição casou mas não levou o movimento.
  shadowedBy: string | null;
}

/// Quem vence é a fila inteira, mas a linha só interessa ao teste se a regra em edição casou: é ela
/// que está sendo escrita. Quando outra vem antes, o teste diz isso em vez de esconder.
function shadowOf(outcome: RuleTrialOutcome, candidateId: string): string | null {
  if (!outcome.hitIds.includes(candidateId)) return null;
  return outcome.ruleId === candidateId ? null : outcome.ruleName;
}

/// A mesma identidade que o servidor usa para a família. É por ela que o formulário reencontra a
/// despesa apontada por uma regra já gravada.
function familyKeyOf(target: ReconciliationRuleTarget): string {
  return target.kind === 'RECURRENCE'
    ? `RECURRENCE:${target.recurrenceRuleId}`
    : `SOURCE_FAMILY:${target.sourceKey}:${target.entity}:${target.externalId}`;
}

/// A condição escrita como a pessoa a leria em voz alta. No modo avançado não há tradução possível:
/// mostrar a expressão é mais honesto do que inventar uma frase para ela.
function conditionOf(rule: ReconciliationRule): string {
  if (rule.mode === 'REGEX') return `expressão regular ${rule.pattern}`;
  const label = operatorLabels.get(rule.operator ?? 'CONTAINS') ?? 'contém';
  return `${label} ${rule.terms.map((term) => `"${term}"`).join(', ')}`;
}

function filtersOf(rule: ReconciliationRule, accounts: FinanceAccount[]): string[] {
  const account = accounts.find((item) => item.id === rule.financeAccountId);
  return [
    ...(rule.direction ? [rule.direction === 'OUTFLOW' ? 'só saídas' : 'só entradas'] : []),
    ...(account ? [account.displayName] : []),
    ...(rule.minAmountCents === null
      ? []
      : [`a partir de ${formatCurrency(rule.minAmountCents, account?.currency ?? 'BRL')}`]),
    ...(rule.maxAmountCents === null
      ? []
      : [`até ${formatCurrency(rule.maxAmountCents, account?.currency ?? 'BRL')}`]),
  ];
}

export function ReconciliationRulesPanel({
  accounts,
  capabilities,
  today,
}: {
  accounts: FinanceAccount[];
  capabilities: ReconciliationRuleCapabilities;
  today: CivilDate;
}) {
  const queryClient = useQueryClient();
  const snackbar = useSnackbar();
  const [search, setSearch] = useState('');
  const [editing, setEditing] = useState<ReconciliationRule | null>();
  const [removing, setRemoving] = useState<ReconciliationRule>();
  const [busy, setBusy] = useState(false);

  const rules = useQuery({
    queryKey: ['company', 'reconciliation-rules'],
    queryFn: () => reconciliationRulesApi.list(),
    retry: false,
  });
  const families = useQuery({
    queryKey: ['company', 'reconciliation-rules', 'families'],
    queryFn: () => reconciliationRulesApi.families(),
    retry: false,
  });

  async function refresh(message: string) {
    await queryClient.invalidateQueries({ queryKey: ['company', 'reconciliation-rules'] });
    await queryClient.invalidateQueries({ queryKey: ['company', 'reconciliation'] });
    setEditing(undefined);
    setRemoving(undefined);
    snackbar.success(message);
  }

  async function move(index: number, step: -1 | 1) {
    const items = rules.data ?? [];
    const target = index + step;
    if (target < 0 || target >= items.length) return;
    const order = items.map((item) => item.id);
    [order[index], order[target]] = [order[target]!, order[index]!];
    setBusy(true);
    try {
      await reconciliationRulesApi.reorder(order);
      await refresh('Ordem das regras atualizada.');
    } catch (error) {
      snackbar.failure(getApiError(error));
    } finally {
      setBusy(false);
    }
  }

  async function remove(rule: ReconciliationRule) {
    setBusy(true);
    try {
      await reconciliationRulesApi.remove(rule.id);
      await refresh('Regra removida.');
    } catch (error) {
      snackbar.failure(getApiError(error));
    } finally {
      setBusy(false);
    }
  }

  const items = rules.data ?? [];
  const shown = items.filter((rule) =>
    matchesListSearch(`${rule.name} ${rule.targetLabel} ${conditionOf(rule)}`, search),
  );
  function ruleActions(rule: ReconciliationRule) {
    const index = items.findIndex((item) => item.id === rule.id);
    return capabilities.canManage ? (
      <div className="flex flex-wrap items-center gap-1">
        <IconButton
          label={`Subir ${rule.name}`}
          icon={<ArrowUp />}
          disabled={busy || index === 0}
          onClick={() => void move(index, -1)}
        />
        <IconButton
          label={`Descer ${rule.name}`}
          icon={<ArrowDown />}
          disabled={busy || index === items.length - 1}
          onClick={() => void move(index, 1)}
        />
        <IconButton label="Editar" icon={<PencilLine />} onClick={() => setEditing(rule)} />
        <IconButton
          label="Remover"
          icon={<Trash2 />}
          tone="danger"
          onClick={() => setRemoving(rule)}
        />
      </div>
    ) : null;
  }
  return (
    <section aria-labelledby="reconciliation-rules-title">
      <Card className="mb-4 flex flex-wrap items-center justify-between gap-3 bg-surface-card">
        <div>
          <h2 id="reconciliation-rules-title" className="text-lg font-semibold">
            Regras de sugestão
          </h2>
          <p className="mt-1 text-sm text-content-muted">
            A primeira regra compatível aponta a despesa provável do pagamento. Ela nunca concilia
            sozinha: a baixa continua nascendo da sua confirmação.
          </p>
        </div>
        {capabilities.canManage ? (
          <Button onClick={() => setEditing(null)}>
            <Plus className="size-4" aria-hidden /> Nova regra
          </Button>
        ) : null}
      </Card>
      {/* O seletor fica à direita, depois da contagem, como nas demais listagens. */}
      <div className="mb-4 flex flex-wrap items-end justify-between gap-3">
        <div className="min-w-0 flex-1">
          <ListFilterBar
            variant="panel"
            searchLabel="Buscar regras de sugestão"
            search={search}
            onSearchChange={setSearch}
            count={`${shown.length} ${shown.length === 1 ? 'regra' : 'regras'}`}
          />
        </div>
        <CollectionViewPicker />
      </div>
      {rules.isLoading ? <LoadingCard label="Carregando regras" /> : null}
      {rules.error ? <ErrorCard message={getApiError(rules.error)} /> : null}
      {!rules.isLoading && !rules.error && items.length === 0 ? (
        <EmptyCard
          icon={Wand2}
          title="Nenhuma regra cadastrada"
          description="Cadastre uma regra para que contas repetidas — energia, água, tributos, parcelas — cheguem já apontadas na conciliação."
        />
      ) : null}
      {items.length > 0 ? (
        <RecordCollection
          label="Regras de conciliação"
          records={items.filter((rule) =>
            matchesListSearch(`${rule.name} ${rule.targetLabel} ${conditionOf(rule)}`, search),
          )}
          recordKey={(rule) => rule.id}
          columns={[
            {
              key: 'priority',
              label: 'Prioridade',
              value: (rule) => items.findIndex((item) => item.id === rule.id),
              render: (rule) => `${String(items.findIndex((item) => item.id === rule.id) + 1)}º`,
            },
            {
              key: 'name',
              label: 'Regra',
              value: (rule) => rule.name,
              render: (rule) => <strong>{rule.name}</strong>,
            },
            {
              key: 'condition',
              label: 'Condição',
              render: (rule) => (
                <div className="text-xs">
                  Quando a descrição {conditionOf(rule)}
                  <p className="text-content-muted">{filtersOf(rule, accounts).join(' · ')}</p>
                </div>
              ),
            },
            {
              key: 'target',
              label: 'Compromisso',
              value: (rule) => rule.targetLabel,
              render: (rule) => rule.targetLabel,
            },
            {
              key: 'status',
              label: 'Situação',
              value: (rule) => Number(rule.active),
              render: (rule) => (
                <StatusPill tone={rule.active ? 'positive' : 'neutral'}>
                  {rule.active ? 'Ativa' : 'Inativa'}
                </StatusPill>
              ),
            },
          ]}
          actions={ruleActions}
          actionsWidth="12rem"
          card={(rule) => {
            const index = items.findIndex((item) => item.id === rule.id);
            return (
              <Card className="flex flex-wrap items-start justify-between gap-4 bg-surface-card">
                <div className="min-w-0 flex-1">
                  <div className="flex flex-wrap items-center gap-2">
                    <span className="text-xs text-content-faint">{index + 1}º</span>
                    <p className="font-semibold">{rule.name}</p>
                    <StatusPill tone={rule.active ? 'positive' : 'neutral'}>
                      {rule.active ? 'Ativa' : 'Inativa'}
                    </StatusPill>
                  </div>
                  <p className="mt-1 text-sm text-content-muted">
                    Quando a descrição {conditionOf(rule)} → {rule.targetLabel}
                  </p>
                  {filtersOf(rule, accounts).length > 0 ? (
                    <p className="mt-1 text-xs text-content-faint">
                      {filtersOf(rule, accounts).join(' · ')}
                    </p>
                  ) : null}
                </div>
                {ruleActions(rule)}
              </Card>
            );
          }}
        />
      ) : null}
      {editing !== undefined ? (
        <RuleDialog
          rule={editing}
          rules={items}
          accounts={accounts}
          today={today}
          families={families.data ?? []}
          onClose={() => setEditing(undefined)}
          onSaved={refresh}
        />
      ) : null}
      {removing ? (
        <Modal
          open
          onOpenChange={() => setRemoving(undefined)}
          title={`Remover ${removing.name}`}
          description="As conciliações já confirmadas continuam como estão; o extrato apenas deixa de receber essa sugestão."
        >
          <div className="mt-6 flex justify-end gap-3">
            <Button variant="outline" onClick={() => setRemoving(undefined)}>
              Voltar
            </Button>
            <Button loading={busy} onClick={() => void remove(removing)}>
              Remover
            </Button>
          </div>
        </Modal>
      ) : null}
    </section>
  );
}

function RuleDialog({
  rule,
  rules,
  accounts,
  families,
  today,
  onClose,
  onSaved,
}: {
  rule: ReconciliationRule | null;
  rules: ReconciliationRule[];
  accounts: FinanceAccount[];
  families: ReconciliationRuleFamily[];
  today: CivilDate;
  onClose: () => void;
  onSaved: (message: string) => Promise<void>;
}) {
  const [name, setName] = useState(rule?.name ?? '');
  const [familyKey, setFamilyKey] = useState(rule ? familyKeyOf(rule.target) : '');
  const [advanced, setAdvanced] = useState(rule?.mode === 'REGEX');
  const [operator, setOperator] = useState<ReconciliationRuleOperator>(
    rule?.operator ?? 'CONTAINS',
  );
  const [terms, setTerms] = useState((rule?.terms ?? []).join(', '));
  const [pattern, setPattern] = useState(rule?.mode === 'REGEX' ? rule.pattern : '');
  const [direction, setDirection] = useState<'' | 'INFLOW' | 'OUTFLOW'>(rule?.direction ?? '');
  const [accountId, setAccountId] = useState(rule?.financeAccountId ?? '');
  const [minAmount, setMinAmount] = useState<number | null>(rule?.minAmountCents ?? null);
  const [maxAmount, setMaxAmount] = useState<number | null>(rule?.maxAmountCents ?? null);
  const [active, setActive] = useState(rule?.active ?? true);
  const [samples, setSamples] = useState('');
  const [includeSettled, setIncludeSettled] = useState(false);
  const [error, setError] = useState<string>();
  const [saving, setSaving] = useState(false);
  const period = useOperationalPeriod(today);

  const family = families.find((item) => item.key === familyKey);
  const target =
    family?.target ?? (rule && familyKeyOf(rule.target) === familyKey ? rule.target : undefined);
  const currency = family?.currency ?? 'BRL';

  /// Só conta, período e situação mudam o que o servidor devolve; direção e faixa de valor são
  /// avaliadas aqui mesmo. Por isso digitar não custa uma ida ao servidor: a busca só se refaz
  /// quando um desses quatro muda, e a anterior é abortada pelo sinal da própria consulta.
  const extract = useQuery({
    queryKey: [
      'company',
      'reconciliation-rules',
      'movements',
      accountId,
      period.from ?? null,
      period.to ?? null,
      includeSettled,
    ],
    queryFn: ({ signal }) =>
      reconciliationRulesApi.movements(
        {
          ...(accountId === '' ? {} : { financeAccountId: accountId }),
          ...(period.from ? { from: period.from } : {}),
          ...(period.to ? { to: period.to } : {}),
          includeSettled,
          limit: SCAN_LIMIT,
        },
        signal,
      ),
    placeholderData: keepPreviousData,
    retry: false,
  });

  /// A regra em edição entra onde ela ficaria de verdade: no lugar da que está sendo alterada, ou
  /// no fim quando é nova. Testar precisa mostrar a fila que vai valer, não uma fila hipotética.
  const queue = useMemo<{ prepared: PreparedRule[]; flaw: string | null }>(() => {
    // Regra inativa não sugere nada na conciliação; deixá-la vencer aqui faria o teste prometer um
    // resultado que a tela de verdade nunca daria.
    const stored = rules
      .filter((item) => item.active)
      .map((item) =>
        prepareRule({
          id: item.id,
          name: item.name,
          targetLabel: item.targetLabel,
          condition: {
            pattern: item.pattern,
            direction: item.direction,
            financeAccountId: item.financeAccountId,
            minAmountCents: item.minAmountCents,
            maxAmountCents: item.maxAmountCents,
          },
        }),
      );
    let candidate: PreparedRule;
    try {
      const cleaned = terms
        .split(',')
        .map(normalizeTerm)
        .filter((term) => term.length > 0);
      candidate = prepareRule({
        id: rule?.id ?? 'candidate',
        name: normalizeTerm(name) === '' ? 'a regra em edição' : normalizeTerm(name),
        targetLabel: family?.label ?? rule?.targetLabel ?? 'a despesa apontada',
        condition: {
          pattern: advanced ? assertTypedPattern(pattern) : buildSimplePattern(operator, cleaned),
          direction: direction === '' ? null : direction,
          financeAccountId: accountId === '' ? null : accountId,
          minAmountCents: minAmount,
          maxAmountCents: maxAmount,
        },
      });
    } catch (cause) {
      // Enquanto a expressão está pela metade não há o que casar. Isso não é recusa: a tela avisa
      // em voz baixa e volta a testar assim que a condição fecha.
      return {
        prepared: stored,
        flaw: cause instanceof RuleValidationError ? cause.message : 'Condição ainda incompleta.',
      };
    }
    const index = stored.findIndex((item) => item.id === candidate.id);
    if (index === -1) stored.push(candidate);
    else stored[index] = { ...candidate, targetLabel: stored[index]!.targetLabel };
    return { prepared: stored, flaw: null };
  }, [
    rules,
    rule,
    family,
    name,
    advanced,
    pattern,
    operator,
    terms,
    direction,
    accountId,
    minAmount,
    maxAmount,
  ]);

  const candidateId = rule?.id ?? 'candidate';

  /// O casamento se refaz a cada tecla sobre as linhas que já estão em mãos: o extrato só volta ao
  /// servidor quando o recorte muda.
  const sampleTrials = useMemo<Trial[]>(
    () =>
      samples
        .split('\n')
        .map((line) => line.trim())
        .filter((line) => line.length > 0)
        .slice(0, MAX_SAMPLES)
        .map((text, index) => {
          const outcome = trialOf(queue.prepared, text, null);
          return {
            key: `sample-${String(index)}`,
            text,
            occurredOn: null,
            amountCents: null,
            currency: null,
            outcome,
            shadowedBy: shadowOf(outcome, candidateId),
          };
        }),
    [samples, queue, candidateId],
  );

  /// Só o que casou sobe para a tela. Andar página a página atrás do que combinou é justamente o
  /// trabalho que o testador existe para poupar.
  const matches = useMemo<{ shown: Trial[]; matched: number; scanned: number }>(() => {
    const movements = extract.data?.movements ?? [];
    const hits: Trial[] = [];
    for (const movement of movements) {
      const outcome = trialOf(queue.prepared, movement.description, {
        description: movement.description,
        direction: movement.direction,
        financeAccountId: movement.financeAccountId,
        amountCents: movement.amountCents,
      });
      if (!outcome.hitIds.includes(candidateId)) continue;
      hits.push({
        key: movement.id,
        text: movement.description,
        occurredOn: movement.occurredOn,
        amountCents: movement.amountCents,
        currency: movement.currency,
        outcome,
        shadowedBy: shadowOf(outcome, candidateId),
      });
    }
    return { shown: hits.slice(0, MAX_MATCHES), matched: hits.length, scanned: movements.length };
  }, [extract.data, queue, candidateId]);

  const total = extract.data?.total ?? 0;
  const periodLabel = period.month === 'ALL' ? 'todos os períodos' : formatMonth(period.month);
  const cut =
    total > matches.scanned
      ? ` Olhamos os ${String(matches.scanned)} mais recentes de ${String(total)}.`
      : '';
  const matchNote =
    matches.matched === 0
      ? null
      : `${matches.matched === 1 ? `1 dos ${String(matches.scanned)} movimentos combinou` : `${String(matches.matched)} dos ${String(matches.scanned)} movimentos combinaram`} com a regra.${cut}${matches.matched > MAX_MATCHES ? ` Mostrando os ${String(MAX_MATCHES)} primeiros.` : ''}`;
  const noMatchNote =
    total === 0
      ? `O extrato de ${periodLabel} não tem movimentos nesse recorte.`
      : `Nenhum movimento do extrato de ${periodLabel} combinou com a regra.${cut}`;

  function inputOf(): ReconciliationRuleInput | undefined {
    if (name.trim().length < 2) {
      setError('Dê um nome à regra.');
      return undefined;
    }
    if (!target) {
      setError('Escolha a despesa que essa regra aponta.');
      return undefined;
    }
    const cleaned = terms
      .split(',')
      .map((term) => term.trim())
      .filter((term) => term.length > 0);
    if (!advanced && cleaned.length === 0) {
      setError('Informe o texto que a regra procura no extrato.');
      return undefined;
    }
    if (advanced && pattern.trim().length === 0) {
      setError('Escreva a expressão regular que a regra deve usar.');
      return undefined;
    }
    return {
      name: name.trim(),
      active,
      mode: advanced ? 'REGEX' : 'SIMPLE',
      ...(advanced ? { pattern: pattern.trim() } : { operator, terms: cleaned }),
      direction: direction === '' ? null : direction,
      financeAccountId: accountId === '' ? null : accountId,
      minAmountCents: minAmount,
      maxAmountCents: maxAmount,
      target,
    };
  }

  async function save() {
    const input = inputOf();
    if (!input) return;
    setSaving(true);
    setError(undefined);
    try {
      if (rule) await reconciliationRulesApi.update(rule.id, input);
      else await reconciliationRulesApi.create(input);
      await onSaved(rule ? 'Regra atualizada.' : 'Regra criada.');
    } catch (cause) {
      setError(getApiError(cause));
    } finally {
      setSaving(false);
    }
  }

  return (
    <Modal
      open
      onOpenChange={onClose}
      title={rule ? `Editar ${rule.name}` : 'Nova regra de sugestão'}
      description="A regra aponta a despesa que se repete; o sistema escolhe sozinho o mês em aberto mais antigo dela. Acento e maiúscula não separam uma linha da outra."
    >
      <div className="grid gap-4">
        <div className="grid gap-4 sm:grid-cols-2">
          <Input
            label="Nome da regra"
            value={name}
            onChange={(event) => setName(event.target.value)}
          />
          <Select
            label="Despesa apontada"
            value={familyKey}
            onValueChange={setFamilyKey}
            options={[
              { value: '', label: 'Escolha a despesa' },
              ...families.map((item) => ({
                value: item.key,
                label: `${item.label} — ${item.hint}`,
              })),
            ]}
          />
        </div>
        {advanced ? (
          <div className="grid gap-2">
            <Input
              label="Expressão regular"
              value={pattern}
              onChange={(event) => setPattern(event.target.value)}
            />
            <RegexGuide />
          </div>
        ) : (
          <div className="grid gap-4 sm:grid-cols-[14rem_1fr]">
            <Select
              label="Quando a descrição"
              value={operator}
              onValueChange={(value) => setOperator(value as ReconciliationRuleOperator)}
              options={operators}
            />
            <Input
              label={operator === 'ANY_OF' ? 'Textos separados por vírgula' : 'Texto procurado'}
              value={terms}
              onChange={(event) => setTerms(event.target.value)}
            />
          </div>
        )}
        <div>
          <Button variant="ghost" onClick={() => setAdvanced(!advanced)}>
            {advanced ? 'Voltar ao modo simples' : 'Usar expressão regular'}
          </Button>
        </div>
        <div className="grid gap-4 sm:grid-cols-2">
          <Select
            label="Direção do pagamento"
            value={direction}
            onValueChange={(value) => setDirection(value as '' | 'INFLOW' | 'OUTFLOW')}
            options={[
              { value: '', label: 'Entradas e saídas' },
              { value: 'OUTFLOW', label: 'Só saídas' },
              { value: 'INFLOW', label: 'Só entradas' },
            ]}
          />
          <Select
            label="Conta financeira"
            value={accountId}
            onValueChange={setAccountId}
            options={[
              { value: '', label: 'Todas as contas' },
              ...accounts.map((item) => ({ value: item.id, label: item.displayName })),
            ]}
          />
          <CurrencyInput
            label="Valor mínimo (opcional)"
            currency={currency}
            value={minAmount}
            onValueChange={setMinAmount}
          />
          <CurrencyInput
            label="Valor máximo (opcional)"
            currency={currency}
            value={maxAmount}
            onValueChange={setMaxAmount}
          />
        </div>
        <Select
          label="Situação"
          value={active ? 'ACTIVE' : 'INACTIVE'}
          onValueChange={(value) => setActive(value === 'ACTIVE')}
          options={[
            { value: 'ACTIVE', label: 'Ativa' },
            { value: 'INACTIVE', label: 'Inativa' },
          ]}
        />
        <div className="rounded-xl border border-surface-border bg-surface-soft p-4">
          <p className="text-sm font-semibold">Teste ao vivo</p>
          <p className="mt-1 text-xs text-content-muted">
            O resultado se refaz enquanto você escreve, e nada aqui grava ou concilia. O exemplo
            digitado avalia apenas o texto; direção, conta e faixa de valor entram só na lista do
            extrato, que mostra apenas os movimentos casados por esta regra.
          </p>
          <Textarea
            label="Exemplos (um por linha)"
            className="mt-3"
            rows={3}
            value={samples}
            onChange={(event) => setSamples(event.target.value)}
          />
          {queue.flaw ? (
            <p className="mt-3 text-xs text-warning-400">{queue.flaw}</p>
          ) : (
            <div className="mt-4 grid gap-3">
              <TrialList title="Exemplos digitados" trials={sampleTrials} />
              <div className="flex flex-wrap items-end justify-between gap-3">
                <p className="min-w-48 flex-1 text-xs text-content-muted">
                  Extrato de{' '}
                  <span className="font-semibold text-content">
                    {period.month === 'ALL' ? 'todos os períodos' : formatMonth(period.month)}
                  </span>{' '}
                  — o mês de trabalho escolhido no topo.
                </p>
                <div className="flex flex-wrap items-end gap-2">
                  {period.month === 'ALL' ? null : (
                    <Button variant="ghost" onClick={() => period.setMonth('ALL')}>
                      Ver todos os períodos
                    </Button>
                  )}
                  <Select
                    label="Movimentos do teste"
                    value={includeSettled ? 'ALL' : 'UNLINKED'}
                    onValueChange={(value) => setIncludeSettled(value === 'ALL')}
                    options={[
                      { value: 'UNLINKED', label: 'Ainda sem conciliar' },
                      { value: 'ALL', label: 'Incluir já conciliados' },
                    ]}
                  />
                </div>
              </div>
              {extract.error ? (
                <p className="text-xs text-danger-400">{getApiError(extract.error)}</p>
              ) : (
                <TrialList
                  title="Movimentos que combinaram"
                  trials={matches.shown}
                  loading={extract.isFetching}
                  note={matchNote}
                  empty={noMatchNote}
                />
              )}
            </div>
          )}
        </div>
        {error ? <RequestAlert message={error} /> : null}
      </div>
      <div className="mt-6 flex justify-end gap-3">
        <Button variant="outline" onClick={onClose}>
          Voltar
        </Button>
        <Button loading={saving} onClick={() => void save()}>
          {rule ? 'Salvar regra' : 'Criar regra'}
        </Button>
      </div>
    </Modal>
  );
}

function TrialList({
  title,
  trials,
  loading = false,
  note = null,
  empty = 'Nada para mostrar aqui.',
}: {
  title: string;
  trials: Trial[];
  loading?: boolean;
  note?: string | null;
  empty?: string;
}) {
  return (
    <div aria-busy={loading}>
      <p className="text-xs font-semibold uppercase tracking-wide text-content-faint">{title}</p>
      {note && trials.length > 0 ? <p className="mt-1 text-xs text-content-muted">{note}</p> : null}
      {trials.length === 0 ? (
        <p className="mt-1 text-sm text-content-muted">
          {loading ? 'Buscando movimentos…' : empty}
        </p>
      ) : (
        <ul className={`mt-1 grid gap-2${loading ? ' opacity-60' : ''}`}>
          {trials.map((item) => (
            <li key={item.key} className="text-sm">
              <p className="font-mono text-xs">
                <Highlight text={item.text} start={item.outcome.start} end={item.outcome.end} />
              </p>
              <p className="text-xs text-content-muted">
                {item.outcome.ruleName === null
                  ? 'nenhuma regra casou'
                  : `${item.outcome.ruleName} → ${item.outcome.targetLabel ?? 'despesa apontada'}`}
                {item.occurredOn ? ` · ${formatCivilDate(item.occurredOn)}` : ''}
                {item.amountCents === null || item.currency === null
                  ? ''
                  : ` · ${formatCurrency(item.amountCents, item.currency)}`}
                {item.outcome.conflicts.length > 0
                  ? ` · também casaram: ${item.outcome.conflicts.join(', ')}`
                  : ''}
              </p>
              {item.shadowedBy === null ? null : (
                <p className="text-xs text-warning-400">
                  A regra em edição casou, mas {item.shadowedBy} vem antes na fila.
                </p>
              )}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

function Highlight({
  text,
  start,
  end,
}: {
  text: string;
  start: number | null;
  end: number | null;
}) {
  if (start === null || end === null) return <>{text}</>;
  return (
    <>
      {text.slice(0, start)}
      <mark className="rounded bg-brand-500/20 px-0.5 text-content">{text.slice(start, end)}</mark>
      {text.slice(end)}
    </>
  );
}
